import api from './api';
import { decryptData } from '../utils/encryption';
import { IDecryptedPasswordEntry } from '../types/vault.types';
import { CardDetail } from '../types/cardDetails';
import { BankDetail } from '../types/bankDetails';
import { PersonalDetail } from '../types/personalDetails';

/**
 * AdminService - Handles super admin operations for viewing user items
 * Uses the admin decryption chain: authKey → admin key → org key → vault key
 * Then reuses existing decryption patterns for each item type
 */
export class AdminService {
    /**
     * Decrypt the vault key using admin's privileges
     * This replicates the decryption chain from ActiveUsersList
     */
    private static async getAdminDecryptedVaultKey(userId: string): Promise<{
        vaultKey: string;
        itemsData: any;
    }> {
        // Get admin's encrypted private key and authKey from sessionStorage
        const encryptedPrivateKeyStr = sessionStorage.getItem('encryptedPrivateKey');
        const authKey = sessionStorage.getItem('authKey');

        if (!encryptedPrivateKeyStr || !authKey) {
            throw new Error("Admin's encrypted private key or auth key not found. Please log in again.");
        }

        const encryptedPrivateKey = JSON.parse(encryptedPrivateKeyStr);

        // Import crypto functions
        const { decryptPrivateKey, importPrivateKey, hybridDecrypt } = await import('../utils/rsaCrypto');

        console.log('=== ADMIN VAULT KEY DECRYPTION ===');

        // STEP 1: Decrypt admin's private key using authKey
        const adminPrivateKeyBuffer = await decryptPrivateKey(encryptedPrivateKey, authKey);
        const adminPrivateKey = await importPrivateKey(adminPrivateKeyBuffer);
        console.log('✓ Admin private key decrypted');

        // STEP 2: Fetch user items from backend
        const response = await api.get(`/admin/user/${userId}/items`);
        console.log('Raw response:', response);
        console.log('Response data:', response.data);
        
        // Handle both {data: {data: ...}} and {data: ...} response structures
        const itemsData = response.data?.data || response.data;
        console.log('Items data:', itemsData);

        if (!itemsData || !itemsData.user) {
            console.error('Invalid response structure. ItemsData:', itemsData);
            throw new Error('Invalid response from server');
        }

        // STEP 3: Decrypt org private key using admin's private key
        const encryptedOrgPrivateKey = itemsData.encryptedOrgPrivateKey;
        if (!encryptedOrgPrivateKey) {
            throw new Error('Organization private key not found in response');
        }

        const orgPrivateKeyPem = await hybridDecrypt(
            encryptedOrgPrivateKey.encryptedData,
            encryptedOrgPrivateKey.encryptedKey,
            encryptedOrgPrivateKey.iv,
            adminPrivateKey,
            encryptedOrgPrivateKey.authTag
        );
        const orgPrivateKey = await importPrivateKey(
            new TextEncoder().encode(orgPrivateKeyPem).buffer
        );
        console.log('✓ Org private key decrypted');

        // STEP 4: Decrypt vault key using org's private key
        const orgEncryptedVaultKey = itemsData.orgEncryptedVaultKey;
        if (!orgEncryptedVaultKey) {
            throw new Error('Organization encrypted vault key not found in response');
        }

        const vaultKey = await hybridDecrypt(
            orgEncryptedVaultKey.encryptedData,
            orgEncryptedVaultKey.encryptedKey,
            orgEncryptedVaultKey.iv,
            orgPrivateKey,
            orgEncryptedVaultKey.authTag
        );
        console.log('✓ Vault key decrypted');
        console.log('=== DECRYPTION CHAIN COMPLETE ===');

        return { vaultKey, itemsData };
    }

    /**
     * Helper to convert Buffer objects to string (from backend BYTEA fields)
     */
    private static bufferToString(bufferObj: any): string {
        if (!bufferObj) return '';
        if (typeof bufferObj === 'string') return bufferObj;
        if (bufferObj.type === 'Buffer' && Array.isArray(bufferObj.data)) {
            const uint8Array = new Uint8Array(bufferObj.data);
            const decoder = new TextDecoder('utf-8');
            return decoder.decode(uint8Array);
        }
        return '';
    }

    /**
     * Decrypt passwords using the vault key
     * Follows the same pattern as VaultService.decryptEntry
     */
    private static decryptPasswords(
        passwords: any[],
        vaultKey: string
    ): IDecryptedPasswordEntry[] {
        return passwords.map(password => {
            try {
                const decrypted: any = {
                    id: password.id,
                    title: password.title,
                    website_url: password.website_url,
                    favourite: password.favourite,
                    category_id: password.category_id,
                    created_at: password.created_at,
                    updated_at: password.updated_at,
                    username: '',
                    password: '',
                    notes: ''
                };

                // Decrypt fields if they exist
                if (password.encrypted_username) {
                    const usernameEncrypted = this.bufferToString(password.encrypted_username);
                    decrypted.username = decryptData(usernameEncrypted, vaultKey);
                }

                if (password.encrypted_password) {
                    const passwordEncrypted = this.bufferToString(password.encrypted_password);
                    decrypted.password = decryptData(passwordEncrypted, vaultKey);
                }

                if (password.encrypted_notes) {
                    const notesEncrypted = this.bufferToString(password.encrypted_notes);
                    decrypted.notes = decryptData(notesEncrypted, vaultKey);
                }

                return decrypted;
            } catch (err) {
                console.error(`Error decrypting password ${password.title}:`, err);
                return {
                    ...password,
                    username: '[Decryption failed]',
                    password: '[Decryption failed]',
                    notes: ''
                };
            }
        });
    }

    /**
     * Decrypt card details using the vault key
     * Follows the same pattern as CardDetailsService.decryptCardDetail
     */
    private static decryptCards(
        cards: any[],
        vaultKey: string
    ): CardDetail[] {
        return cards.map(card => {
            try {
                const decrypted: any = {
                    id: card.id,
                    name: card.name,
                    card_type: card.card_type,
                    category_id: card.category_id,
                    favourite: card.favourite,
                    created_at: card.created_at,
                    updated_at: card.updated_at,
                    categories: []
                };

                // Decrypt encrypted fields
                if (card.encrypted_card_holder_name) {
                    decrypted.encrypted_card_holder_name = decryptData(
                        this.bufferToString(card.encrypted_card_holder_name),
                        vaultKey
                    );
                }
                if (card.encrypted_card_number) {
                    decrypted.encrypted_card_number = decryptData(
                        this.bufferToString(card.encrypted_card_number),
                        vaultKey
                    );
                }
                if (card.encrypted_expiration_date) {
                    decrypted.encrypted_expiration_date = decryptData(
                        this.bufferToString(card.encrypted_expiration_date),
                        vaultKey
                    );
                }
                if (card.encrypted_cvv) {
                    decrypted.encrypted_cvv = decryptData(
                        this.bufferToString(card.encrypted_cvv),
                        vaultKey
                    );
                }
                if (card.encrypted_pin) {
                    decrypted.encrypted_pin = decryptData(
                        this.bufferToString(card.encrypted_pin),
                        vaultKey
                    );
                }
                if (card.encrypted_postal_code) {
                    decrypted.encrypted_postal_code = decryptData(
                        this.bufferToString(card.encrypted_postal_code),
                        vaultKey
                    );
                }
                if (card.encrypted_notes) {
                    decrypted.encrypted_notes = decryptData(
                        this.bufferToString(card.encrypted_notes),
                        vaultKey
                    );
                }

                return decrypted;
            } catch (err) {
                console.error('Error decrypting card:', err);
                return card;
            }
        });
    }

    /**
     * Decrypt bank details using the vault key
     * Follows the same pattern as BankDetailsService.decryptBankDetail
     */
    private static decryptBanks(
        banks: any[],
        vaultKey: string
    ): BankDetail[] {
        return banks.map(bank => {
            try {
                const decrypted: any = {
                    id: bank.id,
                    name: bank.name,
                    bank_name: bank.bank_name,
                    account_type: bank.account_type,
                    category_id: bank.category_id,
                    favourite: bank.favourite,
                    created_at: bank.created_at,
                    updated_at: bank.updated_at,
                    categories: []
                };

                // Decrypt encrypted fields
                if (bank.encrypted_routing_number) {
                    decrypted.encrypted_routing_number = decryptData(
                        this.bufferToString(bank.encrypted_routing_number),
                        vaultKey
                    );
                }
                if (bank.encrypted_account_number) {
                    decrypted.encrypted_account_number = decryptData(
                        this.bufferToString(bank.encrypted_account_number),
                        vaultKey
                    );
                }
                if (bank.encrypted_swift_code) {
                    decrypted.encrypted_swift_code = decryptData(
                        this.bufferToString(bank.encrypted_swift_code),
                        vaultKey
                    );
                }
                if (bank.encrypted_iban_number) {
                    decrypted.encrypted_iban_number = decryptData(
                        this.bufferToString(bank.encrypted_iban_number),
                        vaultKey
                    );
                }
                if (bank.encrypted_pin) {
                    decrypted.encrypted_pin = decryptData(
                        this.bufferToString(bank.encrypted_pin),
                        vaultKey
                    );
                }
                if (bank.encrypted_branch_address) {
                    decrypted.encrypted_branch_address = decryptData(
                        this.bufferToString(bank.encrypted_branch_address),
                        vaultKey
                    );
                }
                if (bank.encrypted_branch_phone) {
                    decrypted.encrypted_branch_phone = decryptData(
                        this.bufferToString(bank.encrypted_branch_phone),
                        vaultKey
                    );
                }
                if (bank.encrypted_notes) {
                    decrypted.encrypted_notes = decryptData(
                        this.bufferToString(bank.encrypted_notes),
                        vaultKey
                    );
                }

                return decrypted;
            } catch (err) {
                console.error('Error decrypting bank:', err);
                return bank;
            }
        });
    }

    /**
     * Decrypt personal details using the vault key
     * Follows the same pattern as PersonalDetailsService.decryptPersonalDetail
     */
    private static decryptPersonals(
        personals: any[],
        vaultKey: string
    ): PersonalDetail[] {
        return personals.map(personal => {
            try {
                const decrypted: any = {
                    id: personal.id,
                    name: personal.name,
                    title: personal.title,
                    gender: personal.gender,
                    birthday: personal.birthday,
                    category_id: personal.category_id,
                    favourite: personal.favourite,
                    created_at: personal.created_at,
                    updated_at: personal.updated_at,
                    categories: []
                };

                // Decrypt encrypted fields
                if (personal.encrypted_first_name) {
                    decrypted.encrypted_first_name = decryptData(
                        this.bufferToString(personal.encrypted_first_name),
                        vaultKey
                    );
                }
                if (personal.encrypted_middle_name) {
                    decrypted.encrypted_middle_name = decryptData(
                        this.bufferToString(personal.encrypted_middle_name),
                        vaultKey
                    );
                }
                if (personal.encrypted_last_name) {
                    decrypted.encrypted_last_name = decryptData(
                        this.bufferToString(personal.encrypted_last_name),
                        vaultKey
                    );
                }
                if (personal.encrypted_username) {
                    decrypted.encrypted_username = decryptData(
                        this.bufferToString(personal.encrypted_username),
                        vaultKey
                    );
                }
                if (personal.encrypted_email_address) {
                    decrypted.encrypted_email_address = decryptData(
                        this.bufferToString(personal.encrypted_email_address),
                        vaultKey
                    );
                }
                if (personal.encrypted_phone) {
                    decrypted.encrypted_phone = decryptData(
                        this.bufferToString(personal.encrypted_phone),
                        vaultKey
                    );
                }
                if (personal.encrypted_address_1) {
                    decrypted.encrypted_address_1 = decryptData(
                        this.bufferToString(personal.encrypted_address_1),
                        vaultKey
                    );
                }
                if (personal.encrypted_address_2) {
                    decrypted.encrypted_address_2 = decryptData(
                        this.bufferToString(personal.encrypted_address_2),
                        vaultKey
                    );
                }
                if (personal.encrypted_city_town) {
                    decrypted.encrypted_city_town = decryptData(
                        this.bufferToString(personal.encrypted_city_town),
                        vaultKey
                    );
                }
                if (personal.encrypted_state) {
                    decrypted.encrypted_state = decryptData(
                        this.bufferToString(personal.encrypted_state),
                        vaultKey
                    );
                }
                if (personal.encrypted_zip_postal_code) {
                    decrypted.encrypted_zip_postal_code = decryptData(
                        this.bufferToString(personal.encrypted_zip_postal_code),
                        vaultKey
                    );
                }
                if (personal.encrypted_country) {
                    decrypted.encrypted_country = decryptData(
                        this.bufferToString(personal.encrypted_country),
                        vaultKey
                    );
                }
                if (personal.encrypted_timezone) {
                    decrypted.encrypted_timezone = decryptData(
                        this.bufferToString(personal.encrypted_timezone),
                        vaultKey
                    );
                }
                if (personal.encrypted_notes) {
                    decrypted.encrypted_notes = decryptData(
                        this.bufferToString(personal.encrypted_notes),
                        vaultKey
                    );
                }

                return decrypted;
            } catch (err) {
                console.error('Error decrypting personal detail:', err);
                return personal;
            }
        });
    }

    /**
     * Main method: Fetch and decrypt all user items for admin view
     * Returns data in the same format as normal user's getAllItemsData
     */
    static async getAllUserItems(userId: string): Promise<{
        passwords: IDecryptedPasswordEntry[];
        cardDetails: CardDetail[];
        bankDetails: BankDetail[];
        personalDetails: PersonalDetail[];
        categories: any[];
        userEmail: string;
        vaultName: string;
        counts: {
            passwords: number;
            cardDetails: number;
            bankDetails: number;
            personalDetails: number;
        };
    }> {
        try {
            // Get vault key and items data
            const { vaultKey, itemsData } = await this.getAdminDecryptedVaultKey(userId);

            console.log('=== DECRYPTING ALL ITEMS ===');

            // Decrypt all item types
            const passwords = this.decryptPasswords(itemsData.passwords || [], vaultKey);
            const cardDetails = this.decryptCards(itemsData.cardDetails || [], vaultKey);
            const bankDetails = this.decryptBanks(itemsData.bankDetails || [], vaultKey);
            const personalDetails = this.decryptPersonals(itemsData.personalDetails || [], vaultKey);

            console.log(`✓ Decrypted ${passwords.length} passwords`);
            console.log(`✓ Decrypted ${cardDetails.length} cards`);
            console.log(`✓ Decrypted ${bankDetails.length} banks`);
            console.log(`✓ Decrypted ${personalDetails.length} personal details`);
            console.log('=== ALL ITEMS DECRYPTED ===');

            return {
                passwords,
                cardDetails,
                bankDetails,
                personalDetails,
                categories: itemsData.categories || [],
                userEmail: itemsData.user.email,
                vaultName: itemsData.vault?.name || 'Vault',
                counts: itemsData.counts
            };
        } catch (error) {
            console.error('Error in AdminService.getAllUserItems:', error);
            throw error;
        }
    }

    /**
     * Get user's categories (vaults) with item counts
     */
    static async getUserCategories(userId: string) {
        try {
            const response = await api.get(`/admin/user/${userId}/categories`);
            const data = response.data?.data || response.data;
            
            return {
                userEmail: data.user.email,
                vaultName: data.vault?.name || 'Vault',
                vaultId: data.vault?.id,
                categories: data.categories || [],
                unassignedCounts: data.unassignedCounts || {},
                totalUnassigned: data.totalUnassigned || 0
            };
        } catch (error) {
            console.error('Error fetching user categories:', error);
            throw error;
        }
    }

    /**
     * Get items in a specific category with admin decryption
     */
    static async getCategoryItems(userId: string, categoryId: string) {
        try {
            // Get admin's encrypted private key and authKey from sessionStorage
            const encryptedPrivateKeyStr = sessionStorage.getItem('encryptedPrivateKey');
            const authKey = sessionStorage.getItem('authKey');

            if (!encryptedPrivateKeyStr || !authKey) {
                throw new Error("Admin's encrypted private key or auth key not found. Please log in again.");
            }

            const encryptedPrivateKey = JSON.parse(encryptedPrivateKeyStr);

            // Import crypto functions
            const { decryptPrivateKey, importPrivateKey, hybridDecrypt } = await import('../utils/rsaCrypto');

            console.log('=== ADMIN CATEGORY ITEMS DECRYPTION ===');

            // STEP 1: Decrypt admin's private key using authKey
            const adminPrivateKeyBuffer = await decryptPrivateKey(encryptedPrivateKey, authKey);
            const adminPrivateKey = await importPrivateKey(adminPrivateKeyBuffer);
            console.log('✓ Admin private key decrypted');

            // STEP 2: Fetch category items from backend
            const response = await api.get(`/admin/user/${userId}/category/${categoryId}/items`);
            const itemsData = response.data?.data || response.data;

            if (!itemsData) {
                throw new Error('Invalid response from server');
            }

            // STEP 3: Decrypt org private key using admin's private key
            const encryptedOrgPrivateKey = itemsData.encryptedOrgPrivateKey;
            const orgPrivateKeyPem = await hybridDecrypt(
                encryptedOrgPrivateKey.encryptedData,
                encryptedOrgPrivateKey.encryptedKey,
                encryptedOrgPrivateKey.iv,
                adminPrivateKey,
                encryptedOrgPrivateKey.authTag
            );
            const orgPrivateKey = await importPrivateKey(
                new TextEncoder().encode(orgPrivateKeyPem).buffer
            );
            console.log('✓ Org private key decrypted');

            // STEP 4: Decrypt vault key using org's private key
            const orgEncryptedVaultKey = itemsData.orgEncryptedVaultKey;
            const vaultKey = await hybridDecrypt(
                orgEncryptedVaultKey.encryptedData,
                orgEncryptedVaultKey.encryptedKey,
                orgEncryptedVaultKey.iv,
                orgPrivateKey,
                orgEncryptedVaultKey.authTag
            );
            console.log('✓ Vault key decrypted');

            // STEP 5: Decrypt all items
            const passwords = this.decryptPasswords(itemsData.passwords || [], vaultKey);
            const cardDetails = this.decryptCards(itemsData.cardDetails || [], vaultKey);
            const bankDetails = this.decryptBanks(itemsData.bankDetails || [], vaultKey);
            const personalDetails = this.decryptPersonals(itemsData.personalDetails || [], vaultKey);

            console.log(`✓ Decrypted ${passwords.length} passwords`);
            console.log(`✓ Decrypted ${cardDetails.length} cards`);
            console.log(`✓ Decrypted ${bankDetails.length} banks`);
            console.log(`✓ Decrypted ${personalDetails.length} personal details`);

            return {
                vault: itemsData.vault,
                category: itemsData.category,
                passwords,
                cardDetails,
                bankDetails,
                personalDetails,
                counts: itemsData.counts
            };
        } catch (error) {
            console.error('Error fetching category items:', error);
            throw error;
        }
    }
}

export default AdminService;
